import React, { useState } from 'react';
import {
  Button,
  Confirmation,
  Drawer,
  Dropdown,
  Table,
  Tabs,
  useToasts,
} from 'freemium-ui';
import { get } from 'lodash';
import qs from 'query-string';
import { useTranslation } from 'react-i18next';
import { USER_LIMIT } from 'routes/constants';
import { AccountInfoType } from 'shared/types';
import EmptyState from '../../../../../shared/components/EmptyState';
import {
  FormContainer,
  FormContent,
  FormHeader,
  TabContainer,
} from '../../../../../shared/styles';
import { getText } from '../../../../../shared/text';
import NewUserForm from './components/NewUserForm';
import { useGetCurrentUser } from '../../hooks';
import {
  useDeleteUser,
  useGetActiveUsersCount,
  useGetAllUsersList,
  useGetInviteUsers,
  useResendAccountActivation,
  useResendAccountInvite,
  useUpdateUserRole,
} from './hooks';
import { useAccessControl } from '../../../../../utils/access-control';
import { Label } from '../../styles';
import { RoleType, TabOptionsType, USERS_PER_PAGE, roles } from './constants';
import { StyledTableContainer } from './style';

const {
  settings: { user },
} = getText('EN');

type UserManagementProps = {
  accountInfo?: AccountInfoType;
};

const UserManagement = (props: UserManagementProps) => {
  const { accountInfo } = props;
  const { t } = useTranslation();
  const [, setToast] = useToasts();
  const { role } = useAccessControl();
  const [selectedTab, setSelectedTab] = React.useState<string>();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<any>(null);
  const [isNewUserDrawerOpen, toggleNewUserDrawer] =
    React.useState<boolean>(false);
  const [pageToken, setPageToken] = React.useState<any>({ 0: '' });
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const {
    data: activeUsers,
    isLoading: isLoadingActiveUsers,
    isFetching: isUsersFetching,
    refetch: refetchActiveUsers,
  } = useGetAllUsersList({
    refetchOnWindowFocus: true,
    pageToken: pageToken[pageIndex] || '',
    callback: async (isSuccess: boolean, response: any) => {
      if (isSuccess) {
        const nextURL = get(response, ['_links', 'next', 'href'], '');
        if (nextURL) {
          const parseData: any = qs.parseUrl(nextURL);
          if (parseData.query?.next_token) {
            setPageToken((pgTkn: any) => ({
              ...pgTkn,
              [pageIndex + 1]: parseData.query.next_token,
            }));
          }
        }
      }
    },
  });

  const { data: invitedusers = [], isFetching: isInviteUsersFetching } =
    useGetInviteUsers({ refetchOnWindowFocus: true });
  const { data: loggedInUser, isLoading: isLoadingUser } = useGetCurrentUser(
    {},
  );
  const { mutate: deleteUser } = useDeleteUser({
    setToast,
  });
  const { mutate: resendInvite } = useResendAccountInvite({
    setToast,
  });
  const { mutate: resendActivation } = useResendAccountActivation({
    setToast,
  });
  const { mutate: updateRole } = useUpdateUserRole({ setToast });
  const { data: activeUsersCount, refetch: refetchActiveUsersCount } =
    useGetActiveUsersCount();

  React.useEffect(() => {
    refetchActiveUsersCount();
  }, [invitedusers, activeUsers]);

  const tabOptions: TabOptionsType[] = [
    {
      id: 'all',
      name:
        `${t(`${user.fields?.tabAll?.name}`)} (${
          activeUsersCount?.count ?? 0
        })` || '',
    },
    {
      id: 'pending',
      name:
        `${t(`${user.fields?.tabPending?.name}`)} (${invitedusers?.length})` ||
        '',
    },
  ];

  const columns: Array<any> = [
    {
      name: 'name',
      Header: t(`${user.fields?.colName?.name}`),
      accessor: ({ user }: { user: any }) => (
        <div className="name">
          {user?.first_name || '-'} {user?.middle_name || ''}{' '}
          {user?.last_name || ''}
        </div>
      ),
    },
    {
      name: 'email',
      Header: t(`${user.fields?.colEmail?.name}`),
      accessor: ({ user, email }: { user: any; email?: string }) => {
        const userEmail = user ? user.email : email;
        return (
          <div className="email" title={userEmail}>
            {userEmail}
          </div>
        );
      },
    },
    {
      Header: t(`${user.fields?.colRole?.name}`),
      accessor: ({
        isInvited,
        user: rowUser,
        user_id,
        user_role,
        display_id,
      }: any) => {
        const translatedRoles = roles.map((eRole) => {
          return { ...eRole, name: `${t(eRole.name)}` };
        });
        const selected: RoleType =
          translatedRoles.find((a: RoleType) => a.id === user_role) ||
          translatedRoles[0];

        if (user_role === 'OWNER')
          return t(`${user.fields?.roleOwner?.name}`) || '';
        else if (
          isInvited ||
          loggedInUser?.email === rowUser?.email ||
          role === 'ANALYST'
        )
          return selected.name;
        return (
          <Dropdown
            width="150px"
            defaultSelectedItem={selected}
            emptyItemsText={`${t('no_results_found')}`}
            items={translatedRoles.filter((a: RoleType) => a.id !== 'OWNER')}
            itemToString={(item: RoleType) => item.name}
            onChange={(role: RoleType) =>
              updateRole({
                userId: user_id,
                display_id: display_id,
                user_role: role.id,
              })
            }
          />
        );
      },
    },
    {
      Header: t(`${user.fields?.colStatus?.name}`),
      accessor: ({ user: currentUser }: any) => {
        return currentUser && currentUser?.status === 'ACTIVATED' ? (
          <Label type="active">
            {' '}
            {t(`${user.fields?.statusActive?.name}`)}{' '}
          </Label>
        ) : currentUser && currentUser?.status === 'INACTIVE' ? (
          <Label type="inactive">
            {' '}
            {t(`${user.fields?.statusInActive?.name}`)}{' '}
          </Label>
        ) : (
          <Label type="pending">
            {' '}
            {t(`${user.fields?.statusPending?.name}`)}{' '}
          </Label>
        );
      },
    },
  ];

  const getOptions = (row: any) => {
    const options: any = [];
    const { user_role } = row;
    if (
      user_role !== 'OWNER' &&
      row?.user?.email !== loggedInUser?.email &&
      role !== 'ANALYST'
    ) {
      options.push({
        id: 'delete',
        name: t(`${user.fields?.optionDelete?.name}`),
      });
    }
    // if (row.user?.status === 'ACTIVATED')
    //   options.push({
    //     id: 'deactivate',
    //     name: user.fields?.optionDeactivate?.name,
    //   });
    if (!row?.user || row?.user?.status === 'INACTIVE')
      options.push({
        id: 'resend',
        name: t(`${user.fields?.optionResend?.name}`),
      });
    return options;
  };

  const toggleDeleteModal = () => {
    setIsDeleteOpen(!isDeleteOpen);
  };

  const handleDelete = () => {
    toggleDeleteModal();
    deleteUser(deleteId);
    setDeleteId(null);
  };

  const handleActions = (option: string, row: any) => {
    switch (option) {
      case 'delete':
        toggleDeleteModal();
        setDeleteId({
          userId: row.user_id ?? row.display_id,
          isInvited: !row.user_id,
        });
        break;
      case 'resend':
        {
          if (row.user_id) resendActivation(row.user_id);
          else if (row.display_id) resendInvite(row.display_id);
        }
        break;
      default:
    }
  };

  React.useEffect(() => {
    refetchActiveUsers();
  }, [pageIndex]);

  React.useEffect(() => {
    setSelectedTab(tabOptions[0].id);
  }, []);

  const limit =
    Number(accountInfo?.resource_limit_configuration?.users) || USER_LIMIT;

  const allUsers =
    Number(activeUsersCount?.count) + (invitedusers?.length ?? 0);

  return (
    <>
      <FormContainer>
        <FormHeader hideBorder>
          <div className="left-pane">
            <div>
              <div className="heading">
                {`${t(`${user.layout.heading}`)} (${allUsers || 0}/${limit})`}
              </div>
              <div className="sub-heading">
                {`${t(`${user.layout.description}`)}`}
              </div>
            </div>
          </div>
          <div className="right-pane">
            <Button
              id="invite-user-btn"
              inline
              disabled={(Number(allUsers) || 0) >= limit}
              onClick={(event: React.MouseEvent) => {
                event.preventDefault();
                toggleNewUserDrawer(true);
              }}
            >
              {t(`${user.action?.okButton}`)}
            </Button>
          </div>
        </FormHeader>
        <FormContent style={{ padding: '0 24px' }}>
          <TabContainer padding="0">
            <Tabs
              value={selectedTab}
              onChange={(event, tab) => setSelectedTab(tab.id)}
              options={tabOptions}
            />
          </TabContainer>
          <StyledTableContainer
            isNotEmpty={
              selectedTab === 'all'
                ? activeUsers?.account_users?.length || false
                : false
            }
          >
            <Table
              data={
                selectedTab === 'all'
                  ? activeUsers?.account_users || []
                  : invitedusers || []
              }
              columns={columns}
              hideTableOnEmpty
              count={Number(activeUsersCount?.count) || 0}
              disableRowSelect
              isLoading={isLoadingActiveUsers || !loggedInUser || isLoadingUser}
              loaderStyles={{ minHeight: 'calc(80vh - 256px)' }}
              dataKey="display_id"
              pageSize={selectedTab === 'pending' ? 100 : USERS_PER_PAGE}
              hideBottomPagination
              onMenuOptionSelect={(option: string, row: any) =>
                handleActions(option, row)
              }
              hideFilters
              emptyComponent={
                <EmptyState
                  minHeight="calc(100vh - 304px)"
                  title={`${t('empty_state.title.no_pending_users')}`}
                  imgSrc="empty-state-contacts.svg"
                />
              }
              rowMenuOptions={(rowData: any) => getOptions(rowData)}
              isFetching={isUsersFetching || isInviteUsersFetching}
              pageIndex={pageIndex}
              setPageIndex={(pgNo: number) => setPageIndex(pgNo)}
            />
          </StyledTableContainer>
        </FormContent>
      </FormContainer>
      <Drawer
        isOpen={isNewUserDrawerOpen}
        onDismiss={() => toggleNewUserDrawer(false)}
        isFullHeight
        clickOutToClose={false}
        placement="right"
      >
        <NewUserForm
          count={allUsers || 0}
          limit={limit}
          setSelectedTab={setSelectedTab}
          selectedTab={selectedTab}
          toggleDrawer={(flag: boolean) => {
            toggleNewUserDrawer(flag);
          }}
        />
      </Drawer>
      <Confirmation
        isOpen={isDeleteOpen}
        width="540px"
        onDismiss={toggleDeleteModal}
        title={`${t(`${user.fields?.confirmation?.name}`)}`}
        cancelButtonText={`${t(`${user.fields?.confirmation?.cancelButton}`)}`}
        confirmButtonText={`${t(`${user.fields?.confirmation?.okButton}`)}`}
        onConfirm={handleDelete}
      >
        <div>{t(`${user.fields?.confirmation?.hint}`)}</div>
      </Confirmation>
    </>
  );
};

export default UserManagement;
